<template>
  <div class="mc" data-test="message-composer">
    <!-- The textarea itself lives in the PANEL's template (slot content keeps the panel as its Vue
         context), so `this.$refs.smsTextarea` / `emailTextarea` in the panel is the real <textarea>
         and PatientSidebar's insertAtCursor / init() focus keep working unchanged. -->
    <div class="mc__field"><slot name="field" /></div>

    <div class="mc__bar">
      <div class="mc__status"><slot name="status" /></div>

      <div v-if="hasMenu" ref="insert" class="mc__insert">
        <button
          type="button"
          class="mc__insert-btn"
          aria-haspopup="true"
          :aria-expanded="open ? 'true' : 'false'"
          data-test="composer-insert"
          @click="open = !open"
          @keydown.esc.stop="open = false"
        ><i class="fa fa-plus" aria-hidden="true"></i> Insert</button>

        <div v-if="open" class="mc__pop" role="menu" data-test="composer-insert-menu" @keydown.esc.stop="open = false">
          <div class="mc__pop-h">Variables</div>
          <button
            v-for="v in variables"
            :key="v.name"
            type="button"
            role="menuitem"
            class="mc__var"
            :data-test="`composer-var-${v.name}`"
            @click="pick({ kind: 'variable', name: v.name })"
          >
            <i class="fa mc__var-ic" :class="v.icon" aria-hidden="true"></i>
            <span class="mc__var-label">{{ v.label }}</span>
            <span class="mc__code">{{ codeOf(v) }}</span>
          </button>

          <template v-if="emoji">
            <div class="mc__pop-h mc__pop-h--rule">Emoji</div>
            <div class="mc__emoji-scroll">
              <div v-for="(group, category) in emoji" :key="category" class="mc__emoji-group">
                <div class="mc__cat">{{ category }}</div>
                <div class="mc__emoji-grid">
                  <button
                    v-for="(char, name) in group"
                    :key="name"
                    type="button"
                    role="menuitem"
                    class="mc__emoji"
                    :title="name"
                    :aria-label="name"
                    @click="pick({ kind: 'emoji', emoji: char })"
                  >{{ char }}</button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The message box shared by Send a text, Send an email and the form-send step (design "Patient
 * Action Panels" 2c / 2d / 2e): the field, a status line under it, and an Insert menu on the right.
 *
 *   slot `field`    the panel's own <textarea> (see the note in the template)
 *   slot `status`   left of Insert — SMS: the <SmsGuidance> credit line; email: the variables hint
 *   variables       [{ name, label, icon }] — the SAME eight the classic + Insert menu offers
 *   emoji           { 'Frequently Used': { thumbs_up: '👍', … }, … } — SMS only; null hides the group
 *
 * Emits `insert` with { kind: 'variable', name } or { kind: 'emoji', emoji }; the panel maps it to
 * PatientSidebar's insertVariable / insertEmoji / insertEmailVariable (which splice at the caret).
 * Presentational: no store, no mixin. ⛔ No prop named patNum / siteID / loading / allSettings.
 */
export default {
  name: 'MessageComposer',

  props: {
    variables: { type: Array, default: () => [] },
    emoji: { type: Object, default: null },
  },

  data() {
    return { open: false };
  },

  computed: {
    hasMenu() {
      return this.variables.length > 0 || !!this.emoji;
    },
  },

  watch: {
    open(isOpen) {
      if (isOpen) document.addEventListener('click', this.onOutside, true);
      else document.removeEventListener('click', this.onOutside, true);
    },
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onOutside, true);
  },

  methods: {
    codeOf(v) {
      return `{{${v.name}}}`;
    },
    pick(payload) {
      this.open = false;
      this.$emit('insert', payload);
    },
    // A tap anywhere outside the menu (and its button) closes it.
    onOutside(e) {
      const root = this.$refs.insert;
      if (root && !root.contains(e.target)) this.open = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './action-panel';

.mc { display: flex; flex-direction: column; gap: 8px; position: relative; }

// The field is the panel's own element, so it is styled with ::v-deep. The size comes from the shared
// mixin only (14px desktop, 16px on touch — iOS focus-zoom).
.mc__field ::v-deep textarea { @include ap-textarea; }

// Insert stays on the first line when the status grows (the SMS guidance banner is several lines).
.mc__bar {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.mc__status {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 7px;
  font-size: 12.5px;
  color: $pp-ink-3;

  @include ap-touch { padding-top: 13px; }
}

.mc__insert-btn {
  @include ap-btn-ghost;
  flex: none;
  height: 34px;
  padding: 0 10px;
  font-size: 13px;

  @include ap-touch { height: 44px; }
}

.mc__pop {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 5;
  width: 292px;
  max-width: 100%;
  max-height: 340px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid $pp-line;
  border-radius: 12px;
  background: $pp-surface;
  box-shadow: 0 10px 30px rgba(12, 15, 14, 0.14);

  @include ap-phone {
    left: 0;
    width: auto;
  }
}

.mc__pop-h {
  padding: 6px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $pp-ink-3;
}

.mc__pop-h--rule { margin-top: 4px; border-top: 1px solid $pp-line; padding-top: 10px; }

.mc__var {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 36px;
  padding: 6px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: $pp-ink;
  font: inherit;
  font-size: 13.5px;
  text-align: left;
  cursor: pointer;

  &:hover { background: $pp-bg; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: -2px; }

  @include ap-touch { min-height: $ap-row-h-touch; }
}

.mc__var-ic { flex: none; width: 16px; text-align: center; color: $pp-ink-4; }
.mc__var-label { flex: 1 1 auto; min-width: 0; }
.mc__code { flex: none; font-family: $pp-mono; font-size: 11px; color: $pp-ink-4; }

.mc__cat {
  margin: 8px 8px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: $pp-ink-4;
}

.mc__emoji-grid { display: flex; flex-wrap: wrap; gap: 2px; padding: 0 4px; }

.mc__emoji {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:hover { background: $pp-bg; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: -2px; }

  @include ap-touch { width: 44px; height: 44px; }
}
</style>
